import { api, formatUrl } from '../api';
import { IAddPatientProposalRequest } from './types/add-patient-proposal-request';
import { IUpdatePatientProposal } from './types/update-patient-proposal';
import { INotePatientProposalRequest } from './types/note-patient-proposal';
import pickBy from 'lodash/pickBy';
import { IPatientProposal } from './types/common';
import { ITreatmentOptionsResponse } from './types/treatment-options';

export const PATIENT_PROPOSAL_API_URLS = {
  ADD: '/patient-proposals',
  GET_ALL: '/patient-proposals',
  GET_BY_ID: '/patient-proposals',
  UPDATE: '/patient-proposals',
  NOTE: '/patient-proposals',
  GET_AS_PATIENT: '/patient-proposals',
  TREATMENT_OPTIONS: '/patient-proposals/options',
};

export const patientProposalApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAllPatientProposals: build.query<
      { total: number; patientProposals: IPatientProposal[] },
      { case?: string; proposal?: string }
    >({
      query: (params) =>
        formatUrl(
          // pickBy removes keys with empty values from params
          `${PATIENT_PROPOSAL_API_URLS.GET_ALL}?${new URLSearchParams(pickBy(params))}`,
        ),
    }),
    getPatientProposalById: build.query<IPatientProposal, { id: string }>({
      query: ({ id }) => formatUrl(`${PATIENT_PROPOSAL_API_URLS.GET_BY_ID}/${id}`),
    }),
    addPatientProposal: build.mutation<{ id: string }, IAddPatientProposalRequest>({
      query: (body) => ({
        url: formatUrl(PATIENT_PROPOSAL_API_URLS.ADD),
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Patients', id: 'LIST' },
        { type: 'Proposals', id: 'LIST' },
      ],
    }),
    updatePatientProposal: build.mutation<unknown, { id: string; body: IUpdatePatientProposal }>({
      query: ({ id, body }) => ({
        url: formatUrl(`${PATIENT_PROPOSAL_API_URLS.UPDATE}/${id}`),
        method: 'PUT',
        body,
      }),
    }),
    notePatientProposal: build.mutation<unknown, INotePatientProposalRequest>({
      query: ({ id, note }) => ({
        url: formatUrl(`${PATIENT_PROPOSAL_API_URLS.NOTE}/${id}/notes`),
        method: 'PUT',
        body: note,
      }),
    }),
    getTreatmentOptions: build.query<ITreatmentOptionsResponse, void>({
      query: () => ({
        url: formatUrl(PATIENT_PROPOSAL_API_URLS.TREATMENT_OPTIONS),
      }),
    }),
    getProposalAsPatient: build.query<IPatientProposal, { proposalId: string; token: string }>({
      query: ({ proposalId, token }) => ({
        url: `/user/v1${PATIENT_PROPOSAL_API_URLS.GET_AS_PATIENT}/${proposalId}?token=${token}`,
      }),
    }),
    bookProposalAsPatient: build.mutation<void, { proposalId: string; quote: string; message: string }>({
      query: ({ proposalId, quote, message }) => ({
        url: `/user/v1${PATIENT_PROPOSAL_API_URLS.GET_AS_PATIENT}/${proposalId}/book`,
        method: 'PUT',
        body: { quote, message },
      }),
    }),
  }),
});

export const {
  useGetProposalAsPatientQuery,
  useGetAllPatientProposalsQuery,
  useGetPatientProposalByIdQuery,
  useAddPatientProposalMutation,
  useUpdatePatientProposalMutation,
  useNotePatientProposalMutation,
  useGetTreatmentOptionsQuery,
  useBookProposalAsPatientMutation,
} = patientProposalApi;
